/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import moment from 'moment';
import * as React from 'react';

import { RichText, RichTextComponent } from '@msdyn365-commerce/core';
import { WineClubOffersBusInput } from '../../dataActions/wine-club-offers-bus';
import { IOfferContainerData } from './offer-container.data';
import { IOfferContainerProps } from './offer-container.props.autogenerated';

/**
 *
 * OfferContainer component
 * @extends {React.PureComponent<IOfferContainerProps<IOfferContainerData>>}
 */
class OfferContainer extends React.PureComponent<IOfferContainerProps<IOfferContainerData>> {

    constructor(props: IOfferContainerProps<IOfferContainerData>) {
        super(props);
        props.context.actionContext.update(
            new WineClubOffersBusInput(),
            {
                offerTaken: false,
                offerAcceptedText: props.config.offerAcceptedText,
                wineClubId: props.config.wineClubId
            }
        );
    }

    public render(): JSX.Element | null {
        const {
            isActive,
            startDate,
            endDate,
            offerExpired,
            className
        } = this.props.config;

        let contentJSX: JSX.Element | undefined;
        const dateLocation = this._interpretDateLocation(startDate, endDate);

        // don't render when declared inactive or is before
        if (!isActive || dateLocation === 'BEFORE') { return null; }

        // if current date is after, show expired JSX
        if (dateLocation === 'AFTER') {
            contentJSX = this._renderRichText(offerExpired || '');
        }

        // if all checks were passed and there wasn't any proper addition to the
        // content, render all the slots and use that as content instead
        if (!contentJSX) {
            contentJSX = this._renderSlotItems(this.props.slots.modules);
        }

        return (
            <section className={`${className} offer-container`}>
                {contentJSX}
            </section>
        );
    }

    private _renderRichText(text: RichText): JSX.Element {
        return (
            <RichTextComponent text={text} />
        );
    }

    //------------------------------------------------------
    // Determine whether the current date is between the start
    // and end dates.
    //
    // Start and end dates are optional, so only check if they
    // are present and valid.
    //------------------------------------------------------
    private _interpretDateLocation(startDate: string | undefined, endDate: string | undefined): 'BEFORE' | 'AFTER' | 'CURRENT' {
        const now = moment();

        // Check to see if it is currently before the given start date
        if (startDate) {
            const startDateMoment = moment(startDate, 'MM/DD/YYYY');
            if (startDateMoment.isValid() && now.isBefore(startDateMoment, 'day')) {
                return 'BEFORE';
            }
        }

        // Check to see if it is currently after the given end date
        if (endDate) {
            const endDateMoment = moment(endDate, 'MM/DD/YYYY');
            if (endDateMoment.isValid() && now.isAfter(endDateMoment, 'day')) {
                return 'AFTER';
            }
        }

        // We are inbetween both start and end date
        return 'CURRENT';
    }

    private _renderSlotItems(items: React.ReactNode[]): JSX.Element {
        return (
            <React.Fragment>
                {items.map((slot: React.ReactNode, index: number) => (
                    <React.Fragment key={index}>
                        {slot}
                    </React.Fragment>
                ))}
            </React.Fragment>
        );
    }
}

export default OfferContainer;
